import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface SuperAdminCompany {
  id: string;
  name: string;
  slug: string | null;
  industry: string | null;
  status: string;
  plan: string;
  trial_ends_at: string | null;
  last_active_at: string | null;
  created_at: string;
  owner_id: string;
  size: string | null;
  user_count?: number;
  mrr?: number;
  ltv?: number;
  engagement_score?: number;
}

export interface PlatformStats {
  total_companies: number;
  active_companies: number;
  trial_companies: number;
  suspended_companies: number;
  total_users: number;
  new_users_30d: number;
  daily_active_users: number;
  weekly_active_users: number;
  total_mrr: number;
  total_arr: number;
  trial_conversion_rate: number;
  churn_rate: number;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  performed_by: string;
  performed_by_email: string | null;
  details: unknown;
  ip_address: string | null;
  created_at: string;
}

export function useSuperAdmin() {
  const { user } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<SuperAdminCompany[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);

  const checkSuperAdminStatus = useCallback(async () => {
    if (!user?.email) {
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("superadmin_whitelist")
        .select("*")
        .eq("email", user.email)
        .eq("is_active", true)
        .maybeSingle();

      setIsSuperAdmin(!!data && !error);
    } catch (err) {
      console.error("Error checking superadmin status:", err);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  const fetchCompanies = useCallback(async () => {
    if (!isSuperAdmin) return;

    try {
      // Fetch companies with related data
      const { data: companiesData, error } = await supabase
        .from("companies")
        .select(`
          *,
          profiles:profiles(count),
          billing:company_billing_history(mrr, ltv),
          engagement:company_engagement_metrics(engagement_score)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const enrichedCompanies: SuperAdminCompany[] = (companiesData || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
        industry: c.industry,
        status: c.status || 'active',
        plan: c.plan || 'trial',
        trial_ends_at: c.trial_ends_at,
        last_active_at: c.last_active_at,
        created_at: c.created_at,
        owner_id: c.owner_id,
        size: c.size,
        user_count: c.profiles?.[0]?.count || 0,
        mrr: c.billing?.[0]?.mrr || 0,
        ltv: c.billing?.[0]?.ltv || 0,
        engagement_score: c.engagement?.[0]?.engagement_score || 0,
      }));

      setCompanies(enrichedCompanies);
    } catch (err) {
      console.error("Error fetching companies:", err);
    }
  }, [isSuperAdmin]);

  const fetchPlatformStats = useCallback(async () => {
    if (!isSuperAdmin) return;

    try {
      // Get company counts by status
      const { data: companiesData } = await supabase
        .from("companies")
        .select("status, plan");

      const totalCompanies = companiesData?.length || 0;
      const activeCompanies = companiesData?.filter(c => c.status === 'active').length || 0;
      const trialCompanies = companiesData?.filter(c => c.plan === 'trial').length || 0;
      const suspendedCompanies = companiesData?.filter(c => c.status === 'suspended').length || 0;

      // Get user counts
      const { count: totalUsers } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true });

      // Get new users in last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const { count: newUsers30d } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .gte("created_at", thirtyDaysAgo.toISOString());

      // Get billing totals
      const { data: billingData } = await supabase
        .from("company_billing_history")
        .select("mrr");

      const totalMrr = billingData?.reduce((sum, b) => sum + (Number(b.mrr) || 0), 0) || 0;

      setStats({
        total_companies: totalCompanies,
        active_companies: activeCompanies,
        trial_companies: trialCompanies,
        suspended_companies: suspendedCompanies,
        total_users: totalUsers || 0,
        new_users_30d: newUsers30d || 0,
        daily_active_users: 0,
        weekly_active_users: 0,
        total_mrr: totalMrr,
        total_arr: totalMrr * 12,
        trial_conversion_rate: 0,
        churn_rate: 0,
      });
    } catch (err) {
      console.error("Error fetching platform stats:", err);
    }
  }, [isSuperAdmin]);

  const fetchAuditLog = useCallback(async (limit = 100) => {
    if (!isSuperAdmin) return;

    try {
      const { data, error } = await supabase
        .from("platform_audit_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      setAuditLog(data || []);
    } catch (err) {
      console.error("Error fetching audit log:", err);
    }
  }, [isSuperAdmin]);

  const logAction = useCallback(async (
    action: string,
    entityType: string,
    entityId?: string,
    details?: Record<string, unknown>
  ) => {
    if (!user) return;

    try {
      await supabase.from("platform_audit_log").insert([{
        action,
        entity_type: entityType,
        entity_id: entityId,
        performed_by: user.id,
        performed_by_email: user.email,
        details: (details || {}) as unknown as import("@/integrations/supabase/types").Json,
      }]);
    } catch (err) {
      console.error("Error logging action:", err);
    }
  }, [user]);

  const updateCompanyStatus = useCallback(async (companyId: string, status: string) => {
    try {
      const { error } = await supabase
        .from("companies")
        .update({ status })
        .eq("id", companyId);

      if (error) throw error;

      await logAction("update_status", "company", companyId, { new_status: status });
      await fetchCompanies();
      return true;
    } catch (err) {
      console.error("Error updating company status:", err);
      return false;
    }
  }, [logAction, fetchCompanies]);

  const updateCompanyPlan = useCallback(async (companyId: string, plan: string) => {
    try {
      const { error } = await supabase
        .from("companies")
        .update({ plan })
        .eq("id", companyId);

      if (error) throw error;

      await logAction("update_plan", "company", companyId, { new_plan: plan });
      await fetchCompanies();
      return true;
    } catch (err) {
      console.error("Error updating company plan:", err);
      return false;
    }
  }, [logAction, fetchCompanies]);

  const extendTrial = useCallback(async (companyId: string, days: number) => {
    try {
      const newTrialEnd = new Date();
      newTrialEnd.setDate(newTrialEnd.getDate() + days);

      const { error } = await supabase
        .from("companies")
        .update({ 
          trial_ends_at: newTrialEnd.toISOString(),
          plan: 'trial'
        })
        .eq("id", companyId);

      if (error) throw error;

      await logAction("extend_trial", "company", companyId, { days_extended: days });
      await fetchCompanies();
      return true;
    } catch (err) {
      console.error("Error extending trial:", err);
      return false;
    }
  }, [logAction, fetchCompanies]);

  const deleteCompany = useCallback(async (companyId: string) => {
    try {
      await logAction("delete_company", "company", companyId);

      const { error } = await supabase
        .from("companies")
        .delete()
        .eq("id", companyId);

      if (error) throw error;

      await fetchCompanies();
      return true;
    } catch (err) {
      console.error("Error deleting company:", err);
      return false;
    }
  }, [logAction, fetchCompanies]);

  const addSupportNote = useCallback(async (companyId: string, note: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("company_support_notes")
        .insert({
          company_id: companyId,
          note,
          created_by: user.id,
          created_by_email: user.email,
        });

      if (error) throw error;
      return true;
    } catch (err) {
      console.error("Error adding support note:", err);
      return false;
    }
  }, [user]);

  useEffect(() => {
    checkSuperAdminStatus();
  }, [checkSuperAdminStatus]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCompanies();
      fetchPlatformStats();
      fetchAuditLog();
    }
  }, [isSuperAdmin, fetchCompanies, fetchPlatformStats, fetchAuditLog]);

  return {
    isSuperAdmin,
    loading,
    companies,
    stats,
    auditLog,
    fetchCompanies,
    fetchPlatformStats,
    fetchAuditLog,
    logAction,
    updateCompanyStatus,
    updateCompanyPlan,
    extendTrial,
    deleteCompany,
    addSupportNote,
  };
}
